import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { useCreateNoteBo, useDeleteNoteBo } from "@/api/notes.api";
import { NoteType } from "@/constants/enums/noteType.enum";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

const PRESET_DIMENSIONS = [
  { id: "preset-1", length: 120, width: 80, height: 53 },
  { id: "preset-2", length: 85, width: 65, height: 53 },
];

const CUSTOM_OPTION = "custom";

const parseDimensionsNote = (note?: string) => {
  const match = note?.match(/^(\d+)\s*x\s*(\d+)\s*x\s*(\d+)\s*\/\s*(\d+)\s*kg$/i);
  if (!match) return null;

  const [, length, width, height, weight] = match;
  return {
    length: Number(length),
    width: Number(width),
    height: Number(height),
    weight: Number(weight),
  };
};

const asNumber = (value: string | number | undefined) =>
  typeof value === "number" ? value : undefined;

export const PackagingDimensionsModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation(["boLogistics", "common"]);
  const existingNote = expedition.expeditionNotes?.find(
    (note) => note.type === NoteType.PACKAGING_DIMENSIONS,
  );

  const parsed = useMemo(() => parseDimensionsNote(existingNote?.note), [existingNote?.note]);
  const matchingPreset = parsed
    ? PRESET_DIMENSIONS.find(
        (preset) =>
          preset.length === parsed.length &&
          preset.width === parsed.width &&
          preset.height === parsed.height,
      )
    : undefined;

  const [selected, setSelected] = useState<string>(matchingPreset?.id ?? CUSTOM_OPTION);
  const [customLength, setCustomLength] = useState<number | undefined>(
    !matchingPreset ? parsed?.length : undefined,
  );
  const [customWidth, setCustomWidth] = useState<number | undefined>(
    !matchingPreset ? parsed?.width : undefined,
  );
  const [customHeight, setCustomHeight] = useState<number | undefined>(
    !matchingPreset ? parsed?.height : undefined,
  );
  const [weight, setWeight] = useState<number | undefined>(parsed?.weight);

  const { mutateAsync: createNote, isPending: isCreating } = useCreateNoteBo(
    t("dimensionsModal.createSuccess"),
  );
  const { mutateAsync: deleteNote, isPending: isDeleting } = useDeleteNoteBo();
  const isPending = isCreating || isDeleting;

  const preset = PRESET_DIMENSIONS.find((p) => p.id === selected);
  const dimensions =
    selected === CUSTOM_OPTION
      ? { length: customLength, width: customWidth, height: customHeight }
      : { length: preset?.length, width: preset?.width, height: preset?.height };

  const canSubmit = !!weight && !!dimensions.length && !!dimensions.width && !!dimensions.height;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (existingNote) {
      await deleteNote(existingNote.id);
    }

    await createNote({
      id: expedition.id,
      payload: `${dimensions.length} x ${dimensions.width} x ${dimensions.height} / ${weight}kg`,
      type: NoteType.PACKAGING_DIMENSIONS,
    });

    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("dimensionsModal.title")}
      size="sm"
      dismissible
      footer={
        <div className="flex w-full flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending || !canSubmit}>
            {isPending ? <Loader2 className="size-4 animate-spin" /> : t("dimensionsModal.submit")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup value={selected} onValueChange={setSelected} className="flex flex-col gap-3">
          {PRESET_DIMENSIONS.map((dimensionPreset) => (
            <div key={dimensionPreset.id} className="flex items-center gap-2">
              <RadioGroupItem id={dimensionPreset.id} value={dimensionPreset.id} />
              <Label htmlFor={dimensionPreset.id} className="cursor-pointer font-normal">
                {dimensionPreset.length} × {dimensionPreset.width} × {dimensionPreset.height}{" "}
                <span className="text-gray-400">cm</span>
              </Label>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <RadioGroupItem id={CUSTOM_OPTION} value={CUSTOM_OPTION} />
            <Label htmlFor={CUSTOM_OPTION} className="cursor-pointer font-normal">
              {t("dimensionsModal.customOption")}
            </Label>
          </div>
        </RadioGroup>

        {selected === CUSTOM_OPTION && (
          <div className="flex items-center gap-2 pl-6">
            <UnifiedInput
              inputType="integer"
              placeholder={t("dimensionsModal.lengthPlaceholder")}
              value={customLength}
              onChange={(value) => setCustomLength(asNumber(value))}
              wrapperClassName="w-16"
              className="text-center"
              maxLength={3}
            />
            <span className="text-gray-400">×</span>
            <UnifiedInput
              inputType="integer"
              placeholder={t("dimensionsModal.widthPlaceholder")}
              value={customWidth}
              onChange={(value) => setCustomWidth(asNumber(value))}
              wrapperClassName="w-16"
              className="text-center"
              maxLength={3}
            />
            <span className="text-gray-400">×</span>
            <UnifiedInput
              inputType="integer"
              placeholder={t("dimensionsModal.heightPlaceholder")}
              value={customHeight}
              onChange={(value) => setCustomHeight(asNumber(value))}
              wrapperClassName="w-16"
              className="text-center"
              maxLength={3}
            />
            <span className="text-gray-400">cm</span>
          </div>
        )}

        <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
          <Label>{t("dimensionsModal.weightLabel")}</Label>
          <UnifiedInput
            inputType="integer"
            placeholder=""
            value={weight}
            onChange={(value) => setWeight(asNumber(value))}
            wrapperClassName="w-24"
            rightElement={<span className="text-gray-400">kg</span>}
            maxLength={4}
          />
        </div>
      </div>
    </Modal>
  );
};
